import { useCallback, useEffect, useRef, useState } from 'react';

/** Share cancellation and focus behavior without making the selected place a draft. */
export function useInlinePlaceEdit() {
  const [editing, setEditing] = useState(false);
  const nameRef = useRef<HTMLButtonElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef(false);
  const closeEditor = useCallback(() => {
    restoreFocus.current = true;
    setEditing(false);
  }, []);

  useEffect(() => {
    if (!editing && restoreFocus.current) {
      nameRef.current?.focus();
      restoreFocus.current = false;
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    const dismiss = (event: PointerEvent) => {
      if (editorRef.current && !event.composedPath().includes(editorRef.current)) setEditing(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closeEditor();
    };
    document.addEventListener('pointerdown', dismiss, true);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', dismiss, true);
      document.removeEventListener('keydown', escape, true);
    };
  }, [closeEditor, editing]);

  return { editing, setEditing, nameRef, editorRef, closeEditor };
}
